import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { Pt, SymmetryShape } from '../../../interfaces/symmetry.interface';
import {
  axisEndpoints, boundingRadius, centroid, isAxisOfSymmetry,
  reflectPolygon, toPolygonPoints
} from './symmetry-geometry';

interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * The interactive Mirror Match play surface — a themed SVG panel where
 * the child drags a glowing "mirror beam" across a crystal shape and
 * releases to test whether it's a line of symmetry.
 *
 * This component is deliberately **presentation-only**: it owns the
 * beam gesture, the live reflection preview, and the win/lose feedback
 * animation, and reports the outcome up via `solved` / `missed`. All
 * scoring, turn rotation and round flow live in the parent shell +
 * `MiniGameService`. That split is what lets a future skill game reuse
 * this same "drag → release → evaluate" surface with a different check.
 *
 * Coordinates are SVG viewBox units (0..VIEW_W / 0..VIEW_H); pointer
 * positions are mapped through the live screen CTM, so it behaves
 * identically under mouse (laptop) and touch (smart TV).
 */
@Component({
  selector: 'app-symmetry-stage',
  templateUrl: './symmetry-stage.component.html',
  styleUrls: ['./symmetry-stage.component.scss']
})
export class SymmetryStageComponent implements OnChanges {
  /** The puzzle to render. */
  @Input() shape: SymmetryShape | null = null;
  /** Bumped by the parent whenever a new round starts — forces a beam
   *  reset even if the shape object identity is unchanged. */
  @Input() roundKey = 0;
  /** When true, faint ghost lines of every true axis are drawn. */
  @Input() hintVisible = false;
  /** Turns off gestures during the resolve animation / between turns. */
  @Input() interactive = true;

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1180;
  readonly VIEW_H = 640;
  /** Match tolerance in viewBox units (~6° angular slack at the shape). */
  private readonly TOL = 20;
  /** How far the beam extends past the shape when snapped / drawn. */
  private readonly BEAM_LEN = 1080;

  /** Shape vertices baked onto the panel centre (absolute viewBox). */
  absPoints: Pt[] = [];
  center: Pt = { x: this.VIEW_W / 2, y: this.VIEW_H / 2 };

  /** The two draggable beam handles (absolute viewBox coords). */
  handleA: Pt = { x: 0, y: 0 };
  handleB: Pt = { x: 0, y: 0 };

  solvedState = false;
  sparkles: Sparkle[] = [];

  private dragTarget: 'A' | 'B' | 'body' | null = null;
  private moved = false;
  private grabPointer: Pt = { x: 0, y: 0 };
  private grabA: Pt = { x: 0, y: 0 };
  private grabB: Pt = { x: 0, y: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shape'] || changes['roundKey']) {
      this.loadShape();
    }
  }

  private loadShape(): void {
    this.solvedState = false;
    this.sparkles = [];
    this.dragTarget = null;
    if (!this.shape) return;

    const cx = this.VIEW_W / 2;
    const cy = this.VIEW_H / 2;
    this.absPoints = this.shape.points.map(p => ({ x: p.x + cx, y: p.y + cy }));
    this.center = centroid(this.absPoints);

    // Start the beam at an off-axis angle, nudged off the centroid, so
    // the puzzle is never pre-solved. 26° avoids coinciding with the
    // common 0/30/45/90/… axis families within tolerance.
    const startPivot: Pt = { x: this.center.x + 55, y: this.center.y - 70 };
    const r = 190;
    const rad = (26 * Math.PI) / 180;
    this.handleA = { x: startPivot.x + Math.cos(rad) * r, y: startPivot.y + Math.sin(rad) * r };
    this.handleB = { x: startPivot.x - Math.cos(rad) * r, y: startPivot.y - Math.sin(rad) * r };
  }

  // ── Derived geometry for the template ──────────────────────────────

  get shapePoints(): string {
    return toPolygonPoints(this.absPoints);
  }

  /** Live mirror image of the shape across the current beam. */
  get reflectionPoints(): string {
    if (this.absPoints.length < 2) return '';
    return toPolygonPoints(reflectPolygon(this.absPoints, this.handleA, this.handleB));
  }

  /** The drawn beam segment — extended well past the handles so it
   *  reads as an infinite mirror line, then clipped to the panel. */
  get beamEnds(): { a: Pt; b: Pt } {
    const dx = this.handleB.x - this.handleA.x;
    const dy = this.handleB.y - this.handleA.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const mid = { x: (this.handleA.x + this.handleB.x) / 2, y: (this.handleA.y + this.handleB.y) / 2 };
    return {
      a: { x: mid.x - ux * this.BEAM_LEN, y: mid.y - uy * this.BEAM_LEN },
      b: { x: mid.x + ux * this.BEAM_LEN, y: mid.y + uy * this.BEAM_LEN }
    };
  }

  /** Ghost lines for every true axis (hint mode). */
  get hintLines(): { a: Pt; b: Pt }[] {
    if (!this.shape) return [];
    return this.shape.axisAngles.map(angle => {
      const [a, b] = axisEndpoints(this.center, angle, this.BEAM_LEN);
      return { a, b };
    });
  }

  // ── Pointer handling (mouse + touch via Pointer Events) ────────────

  onHandleDown(which: 'A' | 'B', evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.beginDrag(which, evt);
  }

  onBodyDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    this.beginDrag('body', evt);
  }

  private beginDrag(target: 'A' | 'B' | 'body', evt: PointerEvent): void {
    this.dragTarget = target;
    this.moved = false;
    this.grabPointer = this.toSvg(evt);
    this.grabA = { ...this.handleA };
    this.grabB = { ...this.handleB };
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TV browsers */ }
  }

  onPointerMove(evt: PointerEvent): void {
    if (!this.dragTarget) return;
    const p = this.toSvg(evt);
    this.moved = true;
    if (this.dragTarget === 'A') {
      this.handleA = this.clamp(p);
    } else if (this.dragTarget === 'B') {
      this.handleB = this.clamp(p);
    } else {
      const dx = p.x - this.grabPointer.x;
      const dy = p.y - this.grabPointer.y;
      this.handleA = this.clamp({ x: this.grabA.x + dx, y: this.grabA.y + dy });
      this.handleB = this.clamp({ x: this.grabB.x + dx, y: this.grabB.y + dy });
    }
  }

  onPointerUp(evt: PointerEvent): void {
    if (!this.dragTarget) return;
    const didMove = this.moved;
    this.dragTarget = null;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
    if (didMove) this.evaluate();
  }

  private evaluate(): void {
    if (!this.shape || this.solvedState) return;
    const ok = isAxisOfSymmetry(this.absPoints, this.handleA, this.handleB, this.TOL);
    if (ok) {
      this.snapToAxis();
      this.solvedState = true;
      this.spawnSparkles();
      this.solved.emit();
    } else {
      // Wrong release is silent — the child is still exploring, so we
      // just leave the beam where they let go and let them keep
      // adjusting. Only success is ever announced. `missed` still fires
      // so the shell can quietly count tries toward offering a hint.
      this.missed.emit();
    }
  }

  /** Lock the beam onto the exact axis (through centroid) for a clean
   *  finished frame. */
  private snapToAxis(): void {
    const dx = this.handleB.x - this.handleA.x;
    const dy = this.handleB.y - this.handleA.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const r = boundingRadius(this.absPoints, this.center) + 40;
    this.handleA = { x: this.center.x + ux * r, y: this.center.y + uy * r };
    this.handleB = { x: this.center.x - ux * r, y: this.center.y - uy * r };
  }

  private spawnSparkles(): void {
    const r = boundingRadius(this.absPoints, this.center);
    this.sparkles = Array.from({ length: 16 }, (_, i) => {
      const a = (i / 16) * Math.PI * 2;
      const dist = r * (0.5 + (i % 3) * 0.22);
      return {
        x: this.center.x + Math.cos(a) * dist,
        y: this.center.y + Math.sin(a) * dist,
        r: 5 + (i % 4) * 3,
        delay: (i % 6) * 40
      };
    });
  }

  private clamp(p: Pt): Pt {
    const m = 12;
    return {
      x: Math.max(m, Math.min(this.VIEW_W - m, p.x)),
      y: Math.max(m, Math.min(this.VIEW_H - m, p.y))
    };
  }

  /** Map a pointer event into SVG viewBox coordinates. */
  private toSvg(evt: PointerEvent): Pt {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const m = pt.matrixTransform(ctm.inverse());
    return { x: m.x, y: m.y };
  }
}
